import { bootGrScrollEngine, GrScrollEngine } from '@theme/gr-scroll-engine';
import { prefersReducedMotion } from '@theme/utilities';

class GrVerticalCovers {
  
  constructor(root) {
    this.root = root;
    this.stage = root.querySelector('[data-stage]');
    this.media = root.querySelector('[data-media]');
    this.covers = Array.from(root.querySelectorAll('[data-cover]'));
    this.titleItems = Array.from(root.querySelectorAll('[data-title-item]'));
    this.titlePrev = root.querySelector('[data-title-prev]');
    this.titleCurrent = root.querySelector('[data-title-current]');
    this.titleNext = root.querySelector('[data-title-next]');
    this.arrow = root.querySelector('[data-scroll-cue]');
    this.count = this.covers.length || Number(root.dataset.count) || 1;
    this.index = 0;

    
    this.triggers = [];
    
    this.arrowTween = null;
    
    this.progressTrigger = null;

    if (!this.stage || this.count === 0) {
      root.classList.add('is-ready');
      return;
    }

    this.onArrowClick = this.onArrowClick.bind(this);
    this.onPrevClick = this.onPrevClick.bind(this);
    this.onCurrentClick = this.onCurrentClick.bind(this);
    this.onNextClick = this.onNextClick.bind(this);
    this.onResize = this.onResize.bind(this);

    this.arrow?.addEventListener('click', this.onArrowClick);
    this.titlePrev?.addEventListener('click', this.onPrevClick);
    this.titleCurrent?.addEventListener('click', this.onCurrentClick);
    this.titleNext?.addEventListener('click', this.onNextClick);
    window.addEventListener('resize', this.onResize);

    // Videos render with preload="none"; only the active one plays, and only a screen ahead.
    this.isNear = !('IntersectionObserver' in window);
    if (!this.isNear) {
      this.nearObserver = new IntersectionObserver(
        (entries) => {
          const near = entries.some((entry) => entry.isIntersecting);
          if (near && !this.isNear) this.onLayoutSettle();
          if (near === this.isNear) return;
          this.isNear = near;
          this.syncVideos();
        },
        { rootMargin: '100% 0px' }
      );
      this.nearObserver.observe(root);
    }

    this.updateTitles(0);
    this.initMotion();
    window.addEventListener('gr-scroll:ready', this.initMotion);
    // Hero intro / landing reveal shift layout; ST start/end go stale without this.
    window.addEventListener('gr-hero:intro-complete', this.onLayoutSettle);
    window.addEventListener('gr-scroll:refresh', this.onLayoutSettle);
  }

  initMotion = () => {
    if (this.triggers.length) return;

    if (prefersReducedMotion()) {
      this.root.classList.add('is-ready');
      if (this.stage) this.stage.style.opacity = '1';
      this.setActive(0, false);
      return;
    }

    bootGrScrollEngine().then((engine) => {
      if (!engine || this.triggers.length) return;
      const gsap = engine.gsap;
      const ScrollTrigger = engine.ScrollTrigger;
      if (!gsap || !ScrollTrigger) {
        this.root.classList.add('is-ready');
        this.setActive(0, false);
        return;
      }

      this.setupScroll(gsap, ScrollTrigger);
      this.root.classList.add('is-ready');
      engine.refresh?.();
      // Second pass after paint — intro/landing may still be settling heights.
      requestAnimationFrame(() => this.onLayoutSettle());
    });
  };

  onLayoutSettle = () => {
    GrScrollEngine.get()?.refresh?.();
    this.bindScrollSync();
    this.syncFromScroll();
  };

  /**
   * Live geometry, not cached ScrollTrigger start/end.
   * After hero intro those values can lag by a full viewport and pin the last cover.
   */
  indexFromGeometry() {
    const range = this.root.offsetHeight - window.innerHeight;
    if (range <= 0 || this.count <= 1) return 0;
    const scrolled = Math.min(range, Math.max(0, -this.root.getBoundingClientRect().top));
    return Math.min(this.count - 1, Math.floor((scrolled / range) * this.count));
  }

  syncFromScroll = () => {
    const next = this.indexFromGeometry();
    if (next !== this.index) this.setActive(next, true);
  };

  /** Keep titles in sync even when ScrollTrigger's range is stale after intro. */
  bindScrollSync() {
    if (this.scrollSyncBound) return;
    const lenis = GrScrollEngine.get()?.lenis;
    if (lenis?.on) {
      lenis.on('scroll', this.syncFromScroll);
      this.scrollSyncBound = true;
      this.scrollSyncVia = 'lenis';
      return;
    }
    window.addEventListener('scroll', this.syncFromScroll, { passive: true });
    this.scrollSyncBound = true;
    this.scrollSyncVia = 'window';
  }

  
  #isLastBeforeFooter() {
    const sectionEl = this.root.closest('.shopify-section');
    const main = sectionEl?.closest('#MainContent, main');
    if (!sectionEl || !main) return false;

    const sections = main.querySelectorAll(':scope > .shopify-section');
    return sections.length > 0 && sections[sections.length - 1] === sectionEl;
  }

  
  setupScroll(gsap, ScrollTrigger) {
    const section = this.root;
    const stage = this.stage;
    const sectionEl = section.closest('.shopify-section');
    const prevSection = sectionEl?.previousElementSibling;
    const isLastBeforeFooter = this.#isLastBeforeFooter();

    if (isLastBeforeFooter) {
      this.root.setAttribute('data-gr-last-before-footer', '');
    }
    const prevSlider =
      prevSection instanceof HTMLElement
        ? prevSection.querySelector('.gr-double-slider, [data-gr-double-slider]')
        : null;

    if (prevSlider instanceof HTMLElement && prevSection instanceof HTMLElement) {
      this.root.setAttribute('data-gr-after-double-slider', '');
      prevSlider.setAttribute('data-gr-before-vertical-covers', '');
      prevSection.setAttribute('data-gr-before-vertical-covers', '');
    }

    if (stage) gsap.set(stage, { opacity: 1 });

    this.progressTrigger = ScrollTrigger.create({
      trigger: section,
      start: 'top top',
      end: 'bottom bottom',
      invalidateOnRefresh: true,
      onUpdate: this.syncFromScroll,
      onRefresh: this.syncFromScroll,
    });
    this.triggers.push(this.progressTrigger);
    this.bindScrollSync();

    if (!isLastBeforeFooter) {
      const exit = gsap.fromTo(
        stage,
        { opacity: 1 },
        {
          opacity: 0,
          ease: 'none',
          scrollTrigger: {
            trigger: section,
            start: 'bottom bottom',
            end: 'bottom top',
            scrub: true,
          },
        }
      );
      if (exit.scrollTrigger) this.triggers.push(exit.scrollTrigger);
    } else if (stage) {
      gsap.set(stage, { opacity: 1 });
    }

    if (this.arrow) {
      this.arrowTween = gsap.fromTo(
        this.arrow,
        { y: 0 },
        {
          y: -10,
          duration: 0.75,
          ease: 'power3.inOut',
          repeat: -1,
          yoyo: true,
        }
      );
    }

    this.setActive(0, false);
  }

  
  getTitleData(index) {
    const item = this.titleItems[index];
    if (!(item instanceof HTMLElement)) {
      return { label: '', link: '', newTab: false };
    }
    return {
      label: item.dataset.label || '',
      link: item.dataset.link || '',
      newTab: item.hasAttribute('data-new-tab'),
    };
  }

  
  setActive(index, _fromScroll) {
    const next = Math.max(0, Math.min(this.count - 1, index));
    this.index = next;

    this.covers.forEach((cover, i) => {
      cover.classList.toggle('is-active', i === next);
    });
    this.syncVideos(true);

    this.updateTitles(next);
  }

  syncVideos(rewindInactive = false) {
    this.covers.forEach((cover, i) => {
      const video = cover.querySelector('video');
      if (!(video instanceof HTMLVideoElement)) return;

      video.muted = true;
      video.playsInline = true;
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');

      if (i === this.index && this.isNear) {
        video.preload = 'auto';
        const play = video.play();
        if (play && typeof play.catch === 'function') play.catch(() => {});
        return;
      }

      if (!video.paused) video.pause();
      if (rewindInactive && i !== this.index) {
        try {
          video.currentTime = 0;
        } catch (_) {
          
        }
      }
    });
  }

  
  updateTitles(index) {
    const current = this.getTitleData(index);
    const hasPrev = index > 0;
    const hasNext = index < this.count - 1;
    const previous = hasPrev ? this.getTitleData(index - 1) : null;
    const upcoming = hasNext ? this.getTitleData(index + 1) : null;

    if (this.titlePrev instanceof HTMLElement) {
      if (previous && previous.label) {
        this.titlePrev.hidden = false;
        this.titlePrev.textContent = previous.label;
        this.titlePrev.setAttribute('aria-label', `Previous: ${previous.label}`);
      } else {
        this.titlePrev.hidden = true;
        this.titlePrev.textContent = '';
      }
    }

    if (this.titleCurrent instanceof HTMLElement) {
      this.titleCurrent.textContent = current.label;
      if (current.link) {
        this.titleCurrent.dataset.hasLink = '';
        this.titleCurrent.setAttribute('aria-label', current.label);
      } else {
        delete this.titleCurrent.dataset.hasLink;
      }
    }

    if (this.titleNext instanceof HTMLElement) {
      if (upcoming && upcoming.label) {
        this.titleNext.hidden = false;
        this.titleNext.textContent = upcoming.label;
        this.titleNext.setAttribute('aria-label', `Next: ${upcoming.label}`);
      } else {
        this.titleNext.hidden = true;
        this.titleNext.textContent = '';
      }
    }
  }

  
  scrollToIndex(index) {
    const engine = GrScrollEngine.get();
    engine?.refresh?.();
    const range = Math.max(0, this.root.offsetHeight - window.innerHeight);
    const scrollY =
      typeof engine?.lenis?.scroll === 'number' ? engine.lenis.scroll : window.scrollY || 0;
    const sectionTop = this.root.getBoundingClientRect().top + scrollY;
    const t = (index + 0.5) / this.count;
    const y = sectionTop + range * t;
    if (engine?.scrollTo) {
      engine.scrollTo(y, { duration: 1.1 });
    } else {
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  }

  onPrevClick(event) {
    event.preventDefault();
    if (this.index > 0) {
      this.scrollToIndex(this.index - 1);
    }
  }

  onCurrentClick(event) {
    event.preventDefault();
    const current = this.getTitleData(this.index);
    if (!current.link) return;
    if (current.newTab) {
      window.open(current.link, '_blank', 'noopener');
      return;
    }
    window.location.href = current.link;
  }

  onNextClick(event) {
    event.preventDefault();
    if (this.index < this.count - 1) {
      this.scrollToIndex(this.index + 1);
    }
  }

  onArrowClick(event) {
    event.preventDefault();
    const st = this.progressTrigger;
    if (!st) return;

    if (this.index < this.count - 1) {
      this.scrollToIndex(this.index + 1);
      return;
    }

    const engine = GrScrollEngine.get();
    const extra = this.root.hasAttribute('data-gr-last-before-footer') ? 0 : window.innerHeight * 0.9;
    const y = st.end + extra;
    if (engine?.scrollTo) engine.scrollTo(y, { duration: 1.1 });
    else window.scrollTo({ top: y, behavior: 'smooth' });
  }

  onResize() {
    this.onLayoutSettle();
  }

  destroy() {
    this.nearObserver?.disconnect();
    if (this.scrollSyncVia === 'lenis') {
      GrScrollEngine.get()?.lenis?.off?.('scroll', this.syncFromScroll);
    } else if (this.scrollSyncVia === 'window') {
      window.removeEventListener('scroll', this.syncFromScroll);
    }
    this.scrollSyncBound = false;
    this.scrollSyncVia = null;
    window.removeEventListener('gr-scroll:ready', this.initMotion);
    window.removeEventListener('gr-hero:intro-complete', this.onLayoutSettle);
    window.removeEventListener('gr-scroll:refresh', this.onLayoutSettle);
    window.removeEventListener('resize', this.onResize);
    this.arrow?.removeEventListener('click', this.onArrowClick);
    this.titlePrev?.removeEventListener('click', this.onPrevClick);
    this.titleCurrent?.removeEventListener('click', this.onCurrentClick);
    this.titleNext?.removeEventListener('click', this.onNextClick);
    this.arrowTween?.kill();
    this.triggers.forEach((t) => t.kill());
    this.triggers = [];
    this.progressTrigger = null;

    this.root.removeAttribute('data-gr-after-double-slider');
    this.root.removeAttribute('data-gr-last-before-footer');
    document
      .querySelectorAll('[data-gr-before-vertical-covers]')
      .forEach((el) => el.removeAttribute('data-gr-before-vertical-covers'));
  }
}

const instances = new WeakMap();

function init() {
  document.querySelectorAll('[data-gr-vertical-covers]').forEach((root) => {
    if (!(root instanceof HTMLElement) || instances.has(root)) return;
    instances.set(root, new GrVerticalCovers(root));
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}

document.addEventListener('shopify:section:load', (event) => {
  const section = event.target;
  if (!(section instanceof HTMLElement)) {
    init();
    return;
  }
  section.querySelectorAll('[data-gr-vertical-covers]').forEach((root) => {
    if (root instanceof HTMLElement) {
      instances.get(root)?.destroy();
      instances.delete(root);
    }
  });
  init();
});
